import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { statusColors } from "./TaskSidebar";
import "../styles/Map.css";

// Fix Leaflet default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Dark map style configuration for OpenStreetMap
const darkMapStyle = {
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  // We'll use CSS to darken the map tiles
};
// Task filter colors (unchanged)
const taskFilterColors = {
  nonaccepter: "#ff0000",
  unassigned: "#ffa500",
  assigned: "#0000ff",
  prete: "#00cc00",
  completed: "#4682b4",
};

// Create task icon for Leaflet
const createTaskIcon = (type, color) => {
  const gradientId = `gradient-${type}-${color.replace("#", "")}`;
  const svgString = `
      <svg width="34" height="46" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="${gradientId}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:${color};stop-opacity:1" />
            <stop offset="100%" style="stop-color:${color};stop-opacity:0.85" />
          </linearGradient>
          <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="3" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.5"/>
          </filter>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
            <feOffset dx="0" dy="0" result="offsetblur"/>
            <feFlood flood-color="${color}" flood-opacity="0.6"/>
            <feComposite in2="offsetblur" operator="in"/>
            <feMerge>
              <feMergeNode/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <g filter="url(#shadow)">
          <path 
            d="M12 2a7.5 7.5 0 0 0-7.5 7.5c0 5.5 7.5 13.5 7.5 13.5s7.5-8 7.5-13.5A7.5 7.5 0 0 0 12 2z" 
            fill="url(#${gradientId})" 
            filter="url(#glow)"
            stroke="#ffffff" 
            stroke-width="0.8"
          />
          <circle cx="11.7" cy="10.5" r="5.5" fill="#ffffff" stroke="#444" stroke-width="0.8"/>
          <text 
            x="12" 
            y="10.5" 
            font-family="Arial, sans-serif" 
            font-size="8" 
            font-weight="bold" 
            fill="#222" 
            text-anchor="middle" 
            dominant-baseline="central"
          >${type}</text>
        </g>
      </svg>
  `;
  
  return L.divIcon({
    html: svgString,
    className: 'custom-div-icon',
    iconSize: [34, 46],
    iconAnchor: [17, 46],
  });
};

// Driver icons for Leaflet
const createDriverIcon = (type) => {
  const iconConfigs = {
    free: {
      size: [12, 12],
      svg: `
        <svg width="12" height="12" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="freeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#00FF00;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#008000;stop-opacity:1" />
            </linearGradient>
            <filter id="shadowFree" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="0.5" stdDeviation="0.5" flood-color="#000000" flood-opacity="0.3"/>
            </filter>
          </defs>
          <circle cx="6" cy="6" r="6" fill="url(#freeGradient)" filter="url(#shadowFree)"/>
        </svg>
      `
    },
    busy: {
      size: [16, 16],
      svg: `
        <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="busyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#1E90FF;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#0000FF;stop-opacity:1" />
            </linearGradient>
            <filter id="shadowBusy" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="0.5" stdDeviation="0.5" flood-color="#000000" flood-opacity="0.3"/>
            </filter>
          </defs>
          <circle cx="8" cy="8" r="8" fill="url(#busyGradient)" filter="url(#shadowBusy)"/>
        </svg>
      `
    },
    livry: {
      size: [14, 14],
      svg: `
        <svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="livryGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#FF69B4;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#C71585;stop-opacity:1" />
            </linearGradient>
            <filter id="shadowLivry" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="0.5" stdDeviation="0.5" flood-color="#000000" flood-opacity="0.3"/>
            </filter>
          </defs>
          <circle cx="7" cy="7" r="7" fill="url(#livryGradient)" filter="url(#shadowLivry)"/>
        </svg>
      `
    },
    glovo: {
      size: [14, 14],
      svg: `
        <svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="glovoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#FFFF00;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#FFD700;stop-opacity:1" />
            </linearGradient>
            <filter id="shadowGlovo" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="0.5" stdDeviation="0.5" flood-color="#000000" flood-opacity="0.3"/>
            </filter>
          </defs>
          <circle cx="7" cy="7" r="7" fill="url(#glovoGradient)" filter="url(#shadowGlovo)"/>
        </svg>
      `
    },
    yassir: {
      size: [14, 14],
      svg: `
        <svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="yassirGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#800080;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#4B0082;stop-opacity:1" />
            </linearGradient>
            <filter id="shadowYassir" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="0.5" stdDeviation="0.5" flood-color="#000000" flood-opacity="0.3"/>
            </filter>
          </defs>
          <circle cx="7" cy="7" r="7" fill="url(#yassirGradient)" filter="url(#shadowYassir)"/>
        </svg>
      `
    }
  };

  const config = iconConfigs[type] || iconConfigs.busy;
  return L.divIcon({
    html: config.svg,
    className: 'custom-driver-icon',
    iconSize: config.size,
    iconAnchor: [config.size[0] / 2, config.size[1] / 2],
  });
};

// Generate unique color for polylines (unchanged)
const generateUniqueColor = (index) => {
  const colors = [
    "#FF4500",
    "#FFD700",
    "#00CED1",
    "#FF69B4",
    "#1E90FF",
    "#32CD32",
    "#BA55D3",
    "#FF6347",
    "#20B2AA",
    "#FF00FF",
  ];
  return colors[index % colors.length];
};

// Component to handle map events
const MapEvents = ({ onCenterChanged, onZoomChanged }) => {
  const map = useMap();
  
  useEffect(() => {
    const handleMoveEnd = () => {
      const center = map.getCenter();
      onCenterChanged({
        lat: center.lat,
        lng: center.lng,
      });
    };

    const handleZoomEnd = () => {
      onZoomChanged(map.getZoom());
    };

    map.on('moveend', handleMoveEnd);
    map.on('zoomend', handleZoomEnd);

    return () => {
      map.off('moveend', handleMoveEnd);
      map.off('zoomend', handleZoomEnd);
    };
  }, [map, onCenterChanged, onZoomChanged]);

  return null;
};

const Map = ({ tasks = {}, drivers = {}, onTaskClick, onDriverClick }) => {
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [mapCenter, setMapCenter] = useState([33.5745214, -7.6119033]); // Leaflet uses [lat, lng] format
  const [mapZoom, setMapZoom] = useState(13);
  const [polylines, setPolylines] = useState([]);
  const [isMapReady, setIsMapReady] = useState(false);
  const [resizeKey, setResizeKey] = useState(0);
  const [filters, setFilters] = useState({
    tasks: {
      nonaccepter: false,
      unassigned: true,
      assigned: true,
      prete: true,
      completed: false,
    },
    drivers: { free: true, busy: true, externe: true },
    search: "",
  });
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(true);
  const mapRef = useRef(null);

  const allDrivers = [
    ...(Array.isArray(drivers.free)
      ? drivers.free.map((driver) => ({ ...driver, status: "FREE" }))
      : []),
    ...(Array.isArray(drivers.busy)
      ? drivers.busy.map((driver) => ({ ...driver, status: "BUSY" }))
      : []),
    ...(Array.isArray(drivers.externe)
      ? drivers.externe.map((driver) => ({
          ...driver,
          status: driver.status || "EXTERNE",
        }))
      : []),
    ...(Array.isArray(drivers.inactive)
      ? drivers.inactive.map((driver) => ({ ...driver, status: "INACTIVE" }))
      : []),
  ].filter((driver) => driver && driver.uuid && driver.lat && driver.lng);

  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const handleCenterChanged = useCallback(
    debounce((center) => {
      setMapCenter([center.lat, center.lng]);
    }, 200),
    []
  );

  const handleZoomChanged = useCallback(
    debounce((zoom) => {
      setMapZoom(zoom);
    }, 200),
    []
  );

  const handleDrawLine = useCallback(
    ({ id, pickup, delivery, filter, isRequest }) => {
      const color = isRequest ? "#ffa500" : generateUniqueColor(polylines.length);
      const newPolyline = {
        id,
        positions: [[pickup.lat, pickup.lng], [delivery.lat, delivery.lng]],
        color: color,
        weight: 4,
        opacity: 0.8,
      };

      setPolylines((prev) => [
        ...prev.filter((polyline) => polyline.id !== id),
        newPolyline,
      ]);

      if (mapRef.current) {
        const bounds = L.latLngBounds([[pickup.lat, pickup.lng], [delivery.lat, delivery.lng]]);
        mapRef.current.fitBounds(bounds);
      }
    },
    [polylines.length]
  );

  const handleRemoveLines = useCallback(() => {
    setPolylines([]);
  }, []);

  const handleCenterOnDriver = useCallback(({ lat, lng, driverId }) => {
    setMapCenter([lat, lng]);
    setMapZoom(16);
    setSelectedDriverId(driverId);
    // Clear any selected task when focusing on driver
    setSelectedTask(null);
    
    // Also directly update the map if it exists
    if (mapRef.current) {
      mapRef.current.setView([lat, lng], 16);
    } else {
      // If map ref is not ready, try again after a short delay
      setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.setView([lat, lng], 16);
        }
      }, 100);
    }
  }, []);

  const handleFocusOnCity = useCallback(({ lat, lng }) => {
    setMapCenter([lat, lng]);
    setMapZoom(13);
  }, []);

  const toggleFilter = useCallback((type, key) => {
    setFilters((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [key]: !prev[type][key],
      },
    }));
  }, []);

  const handleSearchChange = useCallback((e) => {
    setFilters((prev) => ({
      ...prev,
      search: e.target.value.toLowerCase(),
    }));
  }, []);

  useEffect(() => {
    const mapContainer = document.querySelector(".map-container");
    if (mapContainer) {
      const drawLineListener = (event) => {
        handleDrawLine(event.detail);
      };
      const removeLinesListener = () => {
        handleRemoveLines();
      };
      const centerOnDriverListener = (event) => {
        handleCenterOnDriver(event.detail);
      };
      const focusOnCityListener = (event) => {
        handleFocusOnCity(event.detail);
      };

      mapContainer.addEventListener("drawLine", drawLineListener);
      mapContainer.addEventListener("removeLines", removeLinesListener);
      mapContainer.addEventListener("centerOnDriver", centerOnDriverListener);
      mapContainer.addEventListener("focusOnCity", focusOnCityListener);

      return () => {
        mapContainer.removeEventListener("drawLine", drawLineListener);
        mapContainer.removeEventListener("removeLines", removeLinesListener);
        mapContainer.removeEventListener("centerOnDriver", centerOnDriverListener);
        mapContainer.removeEventListener("focusOnCity", focusOnCityListener);
      };
    }
  }, [handleDrawLine, handleRemoveLines, handleCenterOnDriver, handleFocusOnCity]);

  // Map options for Leaflet - optimized for performance
  const mapOptions = {
    zoom: mapZoom,
    center: mapCenter,
    zoomControl: false,
    attributionControl: false,
    preferCanvas: true, // Use canvas renderer for better performance
    zoomAnimation: true,
    fadeAnimation: true,
    markerZoomAnimation: true,
    maxZoom: 18,
    minZoom: 10,
    // Performance optimizations
    renderer: L.canvas(), // Force canvas renderer
    worldCopyJump: false, // Disable world copy jump for better performance
    maxBounds: [
      [33.0, -8.0], // Southwest corner
      [34.0, -7.0]  // Northeast corner
    ],
    maxBoundsViscosity: 1.0, // Keep map within bounds
  };

  const taskFilters = ["nonaccepter", "unassigned", "assigned", "prete", "completed"];
  const allTasks = useMemo(() => {
    return taskFilters.flatMap((filter) =>
      Array.isArray(tasks[filter]) && filters.tasks[filter]
        ? tasks[filter]
            .map((task) => ({ ...task, filter }))
            .filter(
              (task) =>
                filters.search === "" ||
                task.order_id?.toLowerCase().includes(filters.search) ||
                task.designation?.toLowerCase().includes(filters.search) ||
                `${task.prenom || ""} ${task.nom || ""}`
                  .toLowerCase()
                  .includes(filters.search)
            )
        : []
    );
  }, [tasks, filters.tasks, filters.search]);

  const filteredDrivers = useMemo(() => {
    return allDrivers.filter((driver) => {
      if (!driver || !driver.status || !driver.lat || !driver.lng) {
        console.warn("Skipping driver due to missing or invalid data:", driver);
        return false;
      }

      const statusLower = driver.status.toLowerCase();
      const hasType = !!driver.type;
      const isExterne = hasType || statusLower === "externe";

      let isVisible = false;
      if (isExterne) {
        isVisible = filters.drivers.externe;
      } else if (statusLower === "free") {
        isVisible = filters.drivers.free;
      } else if (statusLower === "busy") {
        isVisible = filters.drivers.busy;
      }

      const matchesSearch =
        filters.search === "" ||
        driver.name?.toLowerCase().includes(filters.search) ||
        driver.uuid?.toLowerCase().includes(filters.search);

      return isVisible && matchesSearch;
    });
  }, [allDrivers, filters.drivers, filters.search]);

  const selectedDriver = allDrivers.find((driver) => driver && driver.id === selectedDriverId);

  const handleMapReady = useCallback(() => {
    setIsMapReady(true);
    // Invalidate map size after a short delay to ensure proper rendering
    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 100);
  }, []);

  // Add effect to handle map resize when container size changes
  useEffect(() => {
    const handleResize = () => {
      if (mapRef.current) {
        setTimeout(() => {
          mapRef.current.invalidateSize();
        }, 100);
      }
    };

    // Listen for window resize events
    window.addEventListener('resize', handleResize);
    
    // Also listen for custom events that might indicate sidebar changes
    const mapContainer = document.querySelector(".map-container");
    if (mapContainer) {
      const handleSidebarToggle = () => {
        setResizeKey(prev => prev + 1);
        handleResize();
      };
      
      mapContainer.addEventListener('sidebarToggle', handleSidebarToggle);
      
      // Use ResizeObserver to detect container size changes
      const resizeObserver = new ResizeObserver(() => {
        handleResize();
      });
      
      resizeObserver.observe(mapContainer);
      
      return () => {
        window.removeEventListener('resize', handleResize);
        mapContainer.removeEventListener('sidebarToggle', handleSidebarToggle);
        resizeObserver.disconnect();
      };
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className="map-container">
      {!isMapReady && (
        <div className="map-loading" style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '20px',
          borderRadius: '8px',
          fontSize: '16px'
        }}>
          Loading Map...
        </div>
      )}
      <MapContainer
        key={resizeKey}
        style={{ height: "100vh", width: "100%" }}
        center={mapCenter}
        zoom={mapZoom}
        ref={mapRef}
        whenReady={handleMapReady}
        {...mapOptions}
      >
        <MapEvents onCenterChanged={handleCenterChanged} onZoomChanged={handleZoomChanged} />
        
        {/* Dark gray map with clear roads - free, no auth required */}
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          subdomains={['a', 'b', 'c', 'd']}
          maxZoom={18}
          minZoom={10}
          tileSize={256}
          zoomOffset={0}
          className="map-tiles-enhanced-visible"
          // Performance optimizations
          keepBuffer={2}
          updateWhenZooming={false}
          updateWhenIdle={true}
          noWrap={true}
          bounds={[
            [33.0, -8.0], // Southwest corner
            [34.0, -7.0]  // Northeast corner
          ]}
        />
        {allTasks.length > 0 &&
          allTasks.map((task) => {
            const pickupColor =
              task.status_pickup === "SUCCESS"
                ? "#4caf50"
                : statusColors[task.status_pickup] ||
                  statusColors[task.status_commande] ||
                  statusColors.UNKNOWN;
            const deliveryColor =
              task.status_delivery === "SUCCESS"
                ? "#4caf50"
                : statusColors[task.status_delivery] ||
                  statusColors[task.status_commande] ||
                  statusColors.UNKNOWN;

            return (
              <React.Fragment key={task.id}>
                {task.latitude_pickup && task.longitude_pickup && (
                  <Marker
                    position={[parseFloat(task.latitude_pickup), parseFloat(task.longitude_pickup)]}
                    icon={createTaskIcon("P", pickupColor)}
                    eventHandlers={{
                      click: () => {
                        setSelectedTask({ ...task, type: "P" });
                        if (onTaskClick) {
                          onTaskClick(task.order_id);
                        }
                      },
                    }}
                  >
                    {selectedTask &&
                      selectedTask.id === task.id &&
                      selectedTask.type === "P" && (
                        <Popup onClose={() => setSelectedTask(null)}>
                          <div className="info-window info-window-pickup">
                            <h3 className="info-window-title">Restaurant</h3>
                            <div className="info-window-content">
                              <p>
                                <strong>Name:</strong>{" "}
                                {task.designation || "Unknown Restaurant"}
                              </p>
                              <p>
                                <strong>Phone:</strong>{" "}
                                {task.telephone_restaurant || "N/A"}
                              </p>
                              <p>
                                <strong>Order ID:</strong> {task.order_id || "N/A"}
                              </p>
                              <p>
                                <strong>Status:</strong>{" "}
                                {task.filter.charAt(0).toUpperCase() +
                                  task.filter.slice(1)}
                              </p>
                            </div>
                          </div>
                        </Popup>
                      )}
                  </Marker>
                )}
                {task.latitude_delivery && task.longitude_delivery && (
                  <Marker
                    position={[parseFloat(task.latitude_delivery), parseFloat(task.longitude_delivery)]}
                    icon={createTaskIcon("D", deliveryColor)}
                    eventHandlers={{
                      click: () => {
                        setSelectedTask({ ...task, type: "D" });
                        if (onTaskClick) {
                          onTaskClick(task.order_id);
                        }
                      },
                    }}
                  >
                    {selectedTask &&
                      selectedTask.id === task.id &&
                      selectedTask.type === "D" && (
                        <Popup onClose={() => setSelectedTask(null)}>
                          <div className="info-window info-window-delivery">
                            <h3 className="info-window-title">Client</h3>
                            <div className="info-window-content">
                              <p>
                                <strong>Name:</strong>{" "}
                                {task.prenom && task.nom
                                  ? `${task.prenom} ${task.nom}`
                                  : "Unknown Client"}
                              </p>
                              <p>
                                <strong>Phone:</strong>{" "}
                                {task.telephone_client || "N/A"}
                              </p>
                              <p>
                                <strong>Order ID:</strong> {task.order_id || "N/A"}
                              </p>
                              <p>
                                <strong>Status:</strong>{" "}
                                {task.filter.charAt(0).toUpperCase() +
                                  task.filter.slice(1)}
                              </p>
                              <p>
                                <strong>Address:</strong>{" "}
                                {task.address_delivery || "N/A"}
                              </p>
                            </div>
                          </div>
                        </Popup>
                      )}
                  </Marker>
                )}
              </React.Fragment>
            );
          })}

        {filteredDrivers.map((driver) => {
          if (!driver || !driver.lat || !driver.lng) {
            console.warn(
              `Skipping marker for driver due to missing data:`,
              JSON.stringify(driver, null, 2)
            );
            return null;
          }

          const lat = parseFloat(driver.lat);
          const lng = parseFloat(driver.lng);

          if (isNaN(lat) || isNaN(lng)) {
            console.warn(
              `Skipping marker for driver ${
                driver.name || driver.uuid
              } due to invalid coordinates: Lat=${driver.lat}, Lng=${driver.lng}`
            );
            return null;
          }

          let iconType;

          const isFree = driver.status?.toUpperCase() === "FREE" && driver.connected == 1;

          if (isFree) {
            iconType = "free";
          } else if (driver.type) {
            if (driver.type.toLowerCase() === "livry") {
              iconType = "livry";
            } else if (driver.type.toLowerCase() === "glovo") {
              iconType = "glovo";
            } else if (driver.type.toLowerCase() === "yassir") {
              iconType = "yassir";
            } else {
              iconType = "busy";
            }
          } else {
            iconType = "busy";
          }

          const isSelected = selectedDriverId === driver.id;
          
          return (
            <Marker
              key={driver.id}
              position={[lat, lng]}
              icon={createDriverIcon(iconType)}
              eventHandlers={{
                click: () => {
                  setSelectedDriverId(driver.id);
                  if (onDriverClick) {
                    onDriverClick({
                      lat: lat,
                      lng: lng,
                      driverId: driver.id,
                      driver: driver
                    });
                  }
                },
              }}
            >
              {selectedDriver && selectedDriver.id === driver.id && (
                <Popup onClose={() => setSelectedDriverId(null)}>
                  <div className="info-window info-window-driver">
                    <h3 className="info-window-title">Livreur</h3>
                    <div className="info-window-content">
                      <p>
                        <strong>Name:</strong> {driver.name || "Unknown Driver"}
                      </p>
                      <p>
                        <strong>Status:</strong> {driver.status || "N/A"}
                      </p>
                      <p>
                        <strong>Type:</strong> {driver.type || "N/A"}
                      </p>
                      <p>
                        <strong>Phone:</strong> {driver.telephone || "N/A"}
                      </p>
                      <p>
                        <strong>Orders:</strong> {driver.nb_commande || 0}
                      </p>
                      <p>
                        <strong>Battery:</strong> {driver.battery || "N/A"}%
                      </p>
                      <p>
                        <strong>Location:</strong> {driver.lat}, {driver.lng}
                      </p>
                    </div>
                  </div>
                </Popup>
              )}
            </Marker>
          );
        })}

        {polylines.map((polyline) => (
          <Polyline
            key={polyline.id}
            positions={polyline.positions}
            color={polyline.color}
            weight={polyline.weight}
            opacity={polyline.opacity}
          />
        ))}
      </MapContainer>

      <div className={`map-filter-panel ${isFilterPanelOpen ? "open" : "collapsed"}`}>
        <button className="toggle-filter-button" onClick={() => setIsFilterPanelOpen((prev) => !prev)}>
          {isFilterPanelOpen ? (
            <i className="fas fa-chevron-up"></i>
          ) : (
            <i className="fas fa-chevron-down"></i>
          )}
        </button>
        {isFilterPanelOpen && (
          <div className="filter-content">
            <h4>Filters</h4>
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search by Order ID, Name, etc."
                value={filters.search}
                onChange={handleSearchChange}
              />
            </div>
            <div>
              <h5>Commandes</h5>
              {Object.keys(filters.tasks).map((key) => (
                <label key={key}>
                  <input
                    type="checkbox"
                    checked={filters.tasks[key]}
                    onChange={() => toggleFilter("tasks", key)}
                  />
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </label>
              ))}
            </div>
            <div>
              <h5>Livreurs</h5>
              {Object.keys(filters.drivers).map((key) => (
                <label key={key}>
                  <input
                    type="checkbox"
                    checked={filters.drivers[key]}
                    onChange={() => toggleFilter("drivers", key)}
                  />
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Map;